/**
 * I passi del tutorial guidato dei Carrelli abbandonati.
 *
 * Un solo preparativo: la prima riga va aperta per far vedere cosa contiene il
 * carrello. Si apre nel `before` del passo che ne parla e si richiude in
 * `stopCartAbandonedTour`.
 *
 * I bersagli sono scoped sulla pagina: nella dialog di questa view vivono parecchie
 * schede - ordine, packing list, tracking - e i nomi generici come `tutorial` o
 * `table` sono destinati a ripetersi appena una di quelle avra' un tutorial suo.
 */
const at = name => `[data-tour-scope="cart-abandoned"] [data-tour="${name}"]`;

/** Oltre questo tempo si smette di aspettare che la riga si apra. */
const SETTLE_TIMEOUT_MS = 1200;
const POLL_MS = 50;

function exists(selector) {
	const element = document.querySelector(selector);

	return !!element && element.getClientRects().length > 0;
}

async function waitFor(selector, wanted) {
	const deadline = performance.now() + SETTLE_TIMEOUT_MS;

	while (performance.now() < deadline) {
		if (exists(selector) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, POLL_MS));
	}
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

/** Sempre la prima riga: `querySelector` prende quella, ed e' l'unica che serve. */
async function expandFirstRow() {
	if (exists(at('cart-line'))) {
		return;
	}

	// Niente righe in elenco: non c'e' niente da aprire e non ha senso aspettare.
	if (!exists(at('expand'))) {
		return;
	}

	click(at('expand'));
	await waitFor(at('cart-line'), true);
}

async function collapseFirstRow() {
	if (!exists(at('cart-line'))) {
		return;
	}

	click(at('expand'));
	await waitFor(at('cart-line'), false);
}

/** Rimette l'elenco come l'aveva trovato: righe richiuse. */
export function stopCartAbandonedTour() {
	return collapseFirstRow();
}

export function createCartAbandonedTourSteps() {
	return [
		{
			body: "Qui ci sono i clienti che sul sito hanno messo qualcosa nel carrello e non hanno concluso l'ordine. Non e' una lista che va tenuta pulita a mano: quando l'ordine viene completato, il carrello di quel cliente sparisce da qui da solo. Quello che resta e' quindi gente da ricontattare, con in mano l'elenco esatto di cosa stava per comprare.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "Una riga per cliente, non per carrello: se ha lasciato roba in sospeso piu' volte, la trovi riunita, e Ultima attivita' e' l'ultima volta che ha toccato il carrello, con accanto quanti giorni sono passati. La colonna Carrello mostra le prime immagini, quanti articoli contiene e quanto vale a prezzi di listino attuali. Compaiono solo i clienti riconosciuti, cioe' quelli che avevano fatto l'accesso: chi ha riempito il carrello da visitatore anonimo non e' in questo elenco, perche' non c'e' modo di ricontattarlo. I bot sono esclusi gia' in partenza, quindi quello che leggi sono persone vere.",
			key: 'table',
			placement: 'top',
			target: at('table'),
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "Il piu' a inizio riga apre il carrello di quel cliente. Finche' resta chiuso vedi solo chi e', non cosa voleva - ed e' la parte che serve davvero per decidere se e come ricontattarlo.",
			before: collapseFirstRow,
			key: 'expand',
			placement: 'right',
			target: at('expand'),
			timeout: 250,
			title: 'Aprire il carrello',
		},
		{
			body: "Aperta la riga trovi cosa c'e' dentro il carrello: immagine, nome del prodotto, sku, variante, quantita' e prezzo - una riga per articolo, con il valore stimato in fondo. Il prezzo e' quello di listino di oggi: il carrello non si ricorda quanto costava quando il cliente l'ha riempito. A destra ci sono i contatti e gli indirizzi di spedizione presi dalla sua anagrafica. Il piu' diventa un meno e richiude tutto; puoi anche cliccare in un punto qualsiasi della riga.",
			before: expandFirstRow,
			key: 'cart-line',
			placement: 'right',
			target: at('cart-line'),
			timeout: 250,
			title: 'Cosa c e nel carrello',
		},
		{
			body: "Sopra la tabella si cerca il cliente per nome, cognome o email (da tre caratteri, con Invio o la lente) e si filtra per lingua. Pagina, ricerca e filtri restano nell'indirizzo: un refresh riparte uguale.",
			before: stopCartAbandonedTour,
			key: 'filters',
			placement: 'bottom',
			target: at('filters'),
			title: 'Trovare un carrello',
		},
		{
			body: "I tre pallini a inizio riga raccolgono le azioni sul cliente: Apri scheda cliente (c'e' solo se al carrello corrisponde un'anagrafica), Scrivi su WhatsApp, Scrivi una email e Mostra carrello. La scheda cliente si apre come pagina: Indietro riporta qui, alla stessa pagina e con gli stessi filtri.",
			before: stopCartAbandonedTour,
			key: 'name',
			placement: 'right',
			target: at('row-menu'),
			timeout: 250,
			title: 'Le azioni della riga',
		},
		{
			body: "L'email e' quella dell'account con cui il cliente si era collegato, non una scritta al volo in cassa: e' l'indirizzo giusto su cui mandare un recupero carrello.",
			key: 'email',
			placement: 'bottom',
			target: at('email'),
			timeout: 250,
			title: "L'email del cliente",
		},
		{
			body: "Il tasto verde apre WhatsApp in una nuova scheda, con il messaggio gia' scritto: non parte niente da solo, l'invio lo dai tu. Due cose da sapere. Compare solo per i numeri italiani di dieci cifre - se il cliente ne ha uno straniero o scritto in un formato diverso il tasto non c'e', anche se il numero e' in colonna. Il messaggio precompilato saluta il cliente per nome e gli ricorda il carrello lasciato in sospeso: puoi modificarlo prima di inviare, per esempio citando il prodotto. Il numero, come l'indirizzo, arriva dall'anagrafica del cliente.",
			key: 'whatsapp',
			placement: 'left',
			target: at('whatsapp'),
			timeout: 250,
			title: 'Ricontattare su WhatsApp',
		},
	];
}
